import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LayersIcon from "@mui/icons-material/Layers";
import { ModalOverlay } from "../../../../components/ModalOverlay";
import { findProductLots, ProductLot } from "../../../../api/inventoryService";
import { formatAmount } from "../../purchaseListUtils";

interface Props {
  product: { _id: string; productName?: string; scale?: string; availableQuantity?: number };
  onClose: () => void;
}

const day = (iso: string) => (iso ? iso.slice(0, 10) : "—");

/**
 * The open FIFO lots of one product, oldest first: what is still on hand
 * from each supplier invoice and what it cost. The next release takes from
 * the top row first.
 */
export default function ProductLotsModal({ product, onClose }: Props) {
  const [lots, setLots] = useState<ProductLot[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    findProductLots(product._id)
      .then((data) => alive && setLots(data))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [product._id]);

  const totalQty = (lots || []).reduce((s, l) => s + (Number(l.qtyLeft) || 0), 0);
  const totalValue = (lots || []).reduce((s, l) => s + (Number(l.value) || 0), 0);
  const counterGap = Number(product.availableQuantity ?? totalQty) - totalQty;

  return (
    <ModalOverlay
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Stock lots of ${product.productName || "product"}`}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-100 dark:border-slate-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 bg-primary/10 dark:bg-primary/30 rounded-md flex items-center justify-center shrink-0">
              <LayersIcon className="text-primary dark:text-sky-300" sx={{ fontSize: 14 }} />
            </div>
            <div className="min-w-0">
              <h2 className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">{product.productName}</h2>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">Stock lots, oldest first · the next release takes from the top</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-7 h-7 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {error ? (
            <p className="px-4 py-8 text-center text-xs text-red-500">Could not load the lots.</p>
          ) : lots === null ? (
            <p className="px-4 py-8 text-center text-xs text-slate-400">Loading…</p>
          ) : lots.length === 0 ? (
            <p className="px-4 py-8 text-center text-xs text-slate-400 dark:text-slate-500">
              No stock on hand from any supplier invoice.
            </p>
          ) : (
            <table className="w-full">
              <thead className="border-b border-slate-200 dark:border-slate-600">
                <tr>
                  {["Received", "Invoice", "Supplier", "In", "Left", "Unit cost", "Value"].map((h, i) => (
                    <th
                      key={h}
                      className={`px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider ${i >= 3 ? "text-right" : "text-left"}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                {lots.map((lot, i) => (
                  <tr key={lot._id} className={i === 0 ? "bg-emerald-50/50 dark:bg-emerald-900/20" : ""}>
                    <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-200">{day(lot.receivedAt)}</td>
                    <td className="px-3 py-2 text-xs text-indigo-600 dark:text-indigo-300 font-medium">{lot.invoiceNo || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{lot.supplier || "—"}</td>
                    <td className="px-3 py-2 text-xs text-right text-slate-500 dark:text-slate-400">{lot.qtyIn}</td>
                    <td className="px-3 py-2 text-xs text-right font-semibold text-slate-700 dark:text-slate-200">{lot.qtyLeft}</td>
                    <td className="px-3 py-2 text-xs text-right text-slate-600 dark:text-slate-300">{formatAmount(lot.unitCost)}</td>
                    <td className="px-3 py-2 text-xs text-right font-semibold text-slate-700 dark:text-slate-200">{formatAmount(lot.value)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-slate-200 dark:border-slate-600">
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                    On hand{product.scale ? ` (${product.scale})` : ""}
                  </td>
                  <td className="px-3 py-2 text-xs text-right font-bold text-slate-800 dark:text-slate-100">{totalQty}</td>
                  <td />
                  <td className="px-3 py-2 text-xs text-right font-bold text-emerald-600 dark:text-emerald-300">{formatAmount(totalValue)}</td>
                </tr>
              </tfoot>
            </table>
          )}
          {lots && counterGap > 0 && (
            <p className="px-4 py-2 text-[10px] text-amber-600 dark:text-amber-400 border-t border-slate-100 dark:border-slate-700">
              {counterGap} unit(s) of the stock count have no purchase lot behind them; releases of those units are uncosted.
            </p>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
